// SalesTracker_redesign_like_OrderTracker.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  InputAdornment,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import {
  AddRounded,
  EditRounded,
  DeleteRounded,
  VisibilityRounded,
  SearchRounded,
  FileDownloadRounded,
  AssessmentRounded,
  PendingActionsRounded,
  LoopRounded,
  TaskAltRounded,
  LocalShippingRounded,
  PaymentsRounded,
  RefreshRounded,
  AttachMoneyRounded,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { salesAPI, currencyAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";

// Theme (lean - matches OrderTracker style)
const getTheme = () =>
  createTheme({
    palette: {
      mode: "light",
      primary: { main: "#005b82", light: "#2586bc", dark: "#003d5c" },
      background: { default: "#f8fafc", paper: "#ffffff" },
    },
    shape: { borderRadius: 12 },
  });

const STATUS_OPTS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const SalesTracker = () => {
  const { user, canCreateModule, canUpdateModule, canDeleteModule } = useAuth();
  const [sales, setSales] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingSale, setEditingSale] = useState(null);

  // const [saleForm, setSaleForm] = useState({
  //   sr_no: "",
  //   vessel_name: "",
  //   received_rfq_from_client: "",
  //   issued_quotation_to_client: "",
  //   received_po_from_client: "",
  //   commited_delivery_date_to_client: "",
  //   logistic_status: "",
  //   sales_remark: "",
  //   sales_status: "pending",
  //   issue_pi_to_client: "",
  //   advance_amount_received_from_client: "",
  //   advance_amount_received_date: "",
  //   final_tax_invoice_issued_to_client: "",
  //   final_invoice_date: "",
  //   logistic_cost: "",
  //   final_payment_received_from_client: "",
  //   final_payment_received_date: "",
  //   final_tax_invoice_status: "",
  //   accounts_status: "pending",
  //   accounts_remark: "",
  //   status: "pending",
  //   // remark: "",
  //   // assigned_team: "", // kept commented as requested
  //   currency_advance: "",
  //   currency_logistic: "",
  //   currency_final_payment: "",
  // });
  const emptySaleForm = {
    sr_no: "",
    vessel_name: "",
    received_rfq_date: "",
    rfq_description: "",
    received_rfq_from_client: "",
    issued_quotation_to_client: "",
    received_po_from_client: "",
    commited_delivery_date_to_client: "",
    logistic_status: "",
    sales_remark: "",
    sales_status: "pending",

    // Accounts fields
    issue_pi_to_client: "",
    advance_amount_received_from_client: "",
    advance_amount_received_date: "",
    final_tax_invoice_issued_to_client: "",
    final_invoice_date: "",
    logistic_cost: "",
    final_payment_received_from_client: "",
    final_payment_received_date: "", // ✅ correct field name
    final_tax_invoice_status: "",
    accounts_status: "pending",
    accounts_remark: "",

    // Common
    status: "pending",
    // remark removed – use sales_remark / accounts_remark instead
    // assigned_team: "",

    currency_advance: "",
    currency_logistic: "",
    currency_final_payment: "",
  };

  const [saleForm, setSaleForm] = useState(emptySaleForm);

  // Permissions: create/update/delete follow the live Permissions matrix for
  // the sales_tracker module. Which TEAM's fields are editable within a
  // record (Sales-team vs Accounts-team) is a separate business rule tied to
  // role identity, not a generic module permission, so those stay role-based
  // - matching the same split the backend serializer enforces.
  const perms = {
    canCreate: () => canCreateModule("sales_tracker"),
    canUpdate: () => canUpdateModule("sales_tracker"),
    canEditSales: () => ["SALES", "SUPERADMIN"].includes(user?.role_name),
    canEditAccounts: () => ["ACCOUNTS", "SUPERADMIN"].includes(user?.role_name),
    canEditAll: () => user?.is_superuser || user?.role_name === "SUPERADMIN",
    canDelete: () => canDeleteModule("sales_tracker"),
  };

  const canEditSalesFields =
    perms.canEditSales() &&
    (editingSale ? perms.canUpdate() : perms.canCreate());

  const canEditAccountsFields =
    perms.canEditAccounts() &&
    (editingSale ? perms.canUpdate() : perms.canCreate());

  useEffect(() => {
    (async () => {
      await fetchSales();
      await fetchCurrencies();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const res = await salesAPI.getSales();
      setSales(res.data || []);
    } catch (e) {
      showAlert("Error fetching sales", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const res = await currencyAPI.getCurrencies();
      setCurrencies(res.data || []);
    } catch (e) {
      showAlert("Warning: Failed to load currencies", "warning");
    }
  };

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(
      () => setAlert({ open: false, message: "", severity: "success" }),
      3200,
    );
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

  const fmtMoney = (n, currencyCode = "INR") =>
    n !== null && n !== undefined && String(n) !== ""
      ? (() => {
          try {
            return new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: currencyCode || "INR",
            }).format(Number(n));
          } catch (err) {
            return `${Number(n).toLocaleString("en-IN")} ${currencyCode || ""}`;
          }
        })()
      : "-";

  const statusLabel = (v) => STATUS_OPTS.find((o) => o.value === v)?.label || v;

  const filtered = sales.filter((s) => {
    const q = searchTerm.toLowerCase();
    const matchQ =
      (s.vessel_name || "").toLowerCase().includes(q) ||
      (s.received_rfq_from_client || "").toLowerCase().includes(q) ||
      (s.received_po_from_client || "").toLowerCase().includes(q);
    const matchStatus = statusFilter === "all" || s.status === statusFilter;
    return matchQ && matchStatus;
  });

  const openModal = (sale = null) => {
    if (!sale && !perms.canCreate())
      return showAlert("You do not have permission", "error");

    if (sale) {
      setEditingSale(sale);
      setSaleForm({
        sr_no: sale.sr_no ?? "",
        vessel_name: sale.vessel_name ?? "",
        received_rfq_date: sale.received_rfq_date ?? "",
        rfq_description: sale.rfq_description ?? "",
        received_rfq_from_client: sale.received_rfq_from_client ?? "",
        issued_quotation_to_client: sale.issued_quotation_to_client ?? "",
        received_po_from_client: sale.received_po_from_client ?? "",
        commited_delivery_date_to_client:
          sale.commited_delivery_date_to_client ?? "",
        logistic_status: sale.logistic_status ?? "",
        sales_remark: sale.sales_remark ?? "",
        sales_status: sale.sales_status ?? "pending",
        issue_pi_to_client: sale.issue_pi_to_client ?? "",
        advance_amount_received_from_client:
          sale.advance_amount_received_from_client ?? "",
        advance_amount_received_date: sale.advance_amount_received_date ?? "",
        final_tax_invoice_issued_to_client:
          sale.final_tax_invoice_issued_to_client ?? "",
        final_invoice_date: sale.final_invoice_date ?? "",
        logistic_cost: sale.logistic_cost ?? "",
        final_payment_received_from_client:
          sale.final_payment_received_from_client ?? "",
        final_payment_received_date: sale.final_payment_received_date ?? "",
        final_tax_invoice_status: sale.final_tax_invoice_status ?? "",
        accounts_status: sale.accounts_status ?? "pending",
        accounts_remark: sale.accounts_remark ?? "",
        status: sale.status ?? "pending",
        // remark: sale.remark ?? "",
        currency_advance: sale.currency_advance?.id || "",
        currency_logistic: sale.currency_logistic?.id || "",
        currency_final_payment: sale.currency_final_payment?.id || "",
      });
    } else {
      // new
      setEditingSale(null);
      setSaleForm({ ...emptySaleForm, sr_no: calculateNextSrNo() });
      // setSaleForm((p) => ({ ...p, sr_no: calculateNextSrNo() }));
    }

    setActiveStep(0);
    setOpen(true);
  };

  const calculateNextSrNo = () => {
    if (sales.length === 0) return "1";
    const srNumbers = sales
      .map((s) => {
        if (!s.sr_no) return null;
        const sr = s.sr_no.toString().trim();
        if (!isNaN(sr) && sr !== "") return parseInt(sr, 10);
        const numericPart = sr.match(/\d+$/);
        if (numericPart) return parseInt(numericPart[0], 10);
        return null;
      })
      .filter((sr) => sr !== null && !isNaN(sr));
    if (srNumbers.length > 0) return (Math.max(...srNumbers) + 1).toString();
    return "1";
  };

  const closeModal = () => {
    setOpen(false);
    setEditingSale(null);
  };

  const onChange = (e) =>
    setSaleForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // const onSubmit = async () => {
  //   if (!saleForm.sr_no || !saleForm.vessel_name)
  //     return showAlert("SR No & Vessel required", "error");
  //   try {
  //     if (editingSale) await salesAPI.updateSale(editingSale.id, saleForm);
  //     else await salesAPI.createSale(saleForm);
  //     showAlert(editingSale ? "Updated successfully" : "Created successfully");
  //     await fetchSales();
  //     closeModal();
  //   } catch (e) {
  //     if (e?.response?.data) {
  //       const data = e.response.data;
  //       const msg = Object.entries(data)
  //         .map(([k, v]) => `${k}: ${v.join ? v.join(", ") : v}`)
  //         .join(" ");
  //       showAlert(msg, "error");
  //     } else showAlert("Save error", "error");
  //   }
  // };

  // const onSubmit = async () => {
  //   if (!saleForm.sr_no || !saleForm.vessel_name)
  //     return showAlert("SR No & Vessel required", "error");

  //   try {
  //     // Normalize payload
  //     const payload = { ...saleForm };

  //     [
  //       "commited_delivery_date_to_client",
  //       "advance_amount_received_date",
  //       "final_invoice_date",
  //       "final_payment_received_date",
  //     ].forEach((f) => {
  //       if (payload[f] === "") payload[f] = null;
  //     });

  //     if (editingSale) {
  //       await salesAPI.updateSale(editingSale.id, payload);
  //     } else {
  //       await salesAPI.createSale(payload);
  //     }

  //     showAlert(editingSale ? "Updated successfully" : "Created successfully");
  //     await fetchSales();
  //     closeModal();
  //   } catch (e) {
  //     if (e?.response?.data) {
  //       const data = e.response.data;
  //       const msg = Object.entries(data)
  //         .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
  //         .join(" ");
  //       showAlert(msg, "error");
  //     } else showAlert("Save error", "error");
  //   }
  // };
  const onSubmit = async () => {
    if (!saleForm.sr_no || !saleForm.vessel_name) {
      return showAlert("SR No & Vessel required", "error");
    }

    try {
      // Copy form data
      const payload = { ...saleForm };

      // Convert empty dates to null
      [
        "received_rfq_date", // NEW
        "commited_delivery_date_to_client",
        "advance_amount_received_date",
        "final_invoice_date",
        "final_payment_received_date",
      ].forEach((field) => {
        if (payload[field] === "") {
          payload[field] = null;
        }
      });

      // Convert empty amount fields to null
      [
        "advance_amount_received_from_client",
        "logistic_cost",
        "final_payment_received_from_client",
      ].forEach((field) => {
        if (payload[field] === "") {
          payload[field] = null;
        }
      });

      // Convert empty currency values to null
      [
        "currency_advance",
        "currency_logistic",
        "currency_final_payment",
      ].forEach((field) => {
        if (payload[field] === "") {
          payload[field] = null;
        }
      });

      // Update existing record
      if (editingSale) {
        await salesAPI.updateSale(editingSale.id, payload);
      } else {
        // Create new record
        await salesAPI.createSale(payload);
      }

      showAlert(editingSale ? "Updated successfully" : "Created successfully");

      await fetchSales();
      closeModal();
    } catch (e) {
      console.error("Sales save error:", e?.response?.data || e);

      if (e?.response?.data) {
        const data = e.response.data;

        const msg = Object.entries(data)
          .map(([key, value]) => {
            return `${key}: ${Array.isArray(value) ? value.join(", ") : value}`;
          })
          .join(" ");

        showAlert(msg, "error");
      } else {
        showAlert("Save error", "error");
      }
    }
  };

  const onDelete = async (id) => {
    if (!perms.canDelete()) return showAlert("No permission", "error");
    if (!window.confirm("Delete this record?")) return;
    try {
      await salesAPI.deleteSale(id);
      showAlert("Deleted");
      await fetchSales();
    } catch {
      showAlert("Delete error", "error");
    }
  };

  const exportExcel = async () => {
    try {
      const res = await salesAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "sales_export.xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      showAlert("Excel exported");
    } catch {
      showAlert("Export failed", "error");
    }
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#f8fafc 0%,#e2e8f0 100%)",
          pb: 4,
        }}
      >
        <Snackbar
          open={alert.open}
          autoHideDuration={4000}
          onClose={() => setAlert((a) => ({ ...a, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setAlert((a) => ({ ...a, open: false }))}
            severity={alert.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {alert.message}
          </MuiAlert>
        </Snackbar>

        <Container maxWidth="xl" sx={{ py: 2 }}>
          {/* Stats */}
          <Grid container spacing={4} sx={{ mb: 3 }}>
            {[
              {
                key: "total",
                label: "Total Sales",
                icon: <AssessmentRounded />,
                color: "#005b82",
              },
              {
                key: "pending",
                label: "Pending",
                icon: <PendingActionsRounded />,
                color: "#ff9800",
              },
              {
                key: "in_progress",
                label: "In Progress",
                icon: <LoopRounded />,
                color: "#2196f3",
              },
              {
                key: "closed",
                label: "Closed",
                icon: <TaskAltRounded />,
                color: "#4caf50",
              },
              {
                key: "delivered",
                label: "Delivered",
                icon: <LocalShippingRounded />,
                color: "#4caf50",
              },
              {
                key: "cancelled",
                label: "Cancelled",
                icon: <RefreshRounded />,
                color: "#f44336",
              },
            ].map(({ key, label, icon, color }) => {
              const count =
                key === "total"
                  ? sales.length
                  : sales.filter((s) => s.status === key).length;
              const pct = sales.length ? (count / sales.length) * 100 : 0;
              return (
                <Grid item xs={12} sm={6} md={2} key={key}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            background: alpha(color, 0.1),
                            color,
                          }}
                        >
                          {icon}
                        </Box>
                        <Typography variant="h4" fontWeight={700} color={color}>
                          {count}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          mt: 1,
                          backgroundColor: alpha(color, 0.2),
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: color,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {pct.toFixed(1)}% of total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search sales records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 280, flex: 1 }}
                />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    {STATUS_OPTS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadRounded />}
                    onClick={exportExcel}
                  >
                    Export
                  </Button>
                  {perms.canCreate() && (
                    <Button
                      variant="contained"
                      startIcon={<AddRounded />}
                      onClick={() => openModal()}
                      sx={{
                        background:
                          "linear-gradient(135deg,#005b82 0%,#2586bc 100%)",
                      }}
                    >
                      New Sale
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography color="text.secondary">
                    Loading sales data...
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>SR</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vessel</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>RFQ Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          RFQ Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          RFQ Description
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Quotation
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>PO</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Delivery Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Logistics
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Sales Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Sales Remark
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>PI</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Advance Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Advance Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Final Invoice
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Final Invoice Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Logistic Cost
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Final Payment
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Payment Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Accounts Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Accounts Remark
                        </TableCell>
                        {/* Assigned column kept commented */}
                        {/* <TableCell sx={{ fontWeight:700 }}>Assigned</TableCell> */}
                        {/* <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell> */}
                        <TableCell sx={{ fontWeight: 700 }}>Overall</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell>
                            <Typography fontWeight={600}>{s.sr_no}</Typography>
                          </TableCell>
                          <TableCell>{s.vessel_name}</TableCell>
                          <TableCell>{fmtDate(s.received_rfq_date)}</TableCell>
                          <TableCell>
                            {s.received_rfq_from_client || "-"}
                          </TableCell>
                          <TableCell>{s.rfq_description || "-"}</TableCell>
                          <TableCell>
                            {s.issued_quotation_to_client || "-"}
                          </TableCell>
                          <TableCell>
                            {s.received_po_from_client || "-"}
                          </TableCell>
                          <TableCell>
                            {fmtDate(s.commited_delivery_date_to_client)}
                          </TableCell>
                          <TableCell>{s.logistic_status || "-"}</TableCell>
                          <TableCell>
                            <Chip label={s.sales_status || "-"} size="small" />
                          </TableCell>
                          <TableCell>{s.sales_remark || "-"}</TableCell>
                          <TableCell>{s.issue_pi_to_client || "-"}</TableCell>
                          <TableCell>
                            {fmtMoney(
                              s.advance_amount_received_from_client,
                              s.currency_advance?.code || "INR",
                            )}
                          </TableCell>
                          <TableCell>
                            {fmtDate(s.advance_amount_received_date)}
                          </TableCell>
                          <TableCell>
                            {s.final_tax_invoice_issued_to_client || "-"}
                          </TableCell>
                          <TableCell>{fmtDate(s.final_invoice_date)}</TableCell>
                          <TableCell>
                            {fmtMoney(
                              s.logistic_cost,
                              s.currency_logistic?.code || "INR",
                            )}
                          </TableCell>
                          <TableCell>
                            {fmtMoney(
                              s.final_payment_received_from_client,
                              s.currency_final_payment?.code || "INR",
                            )}
                          </TableCell>
                          <TableCell>
                            {fmtDate(s.final_payment_received_date)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={s.accounts_status || "-"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{s.accounts_remark || "-"}</TableCell>
                          {/* <TableCell>{s.assigned_team || "-"}</TableCell> */}
                          {/* <TableCell>
                            {s.currency_advance?.symbol ||
                              s.currency_advance?.code ||
                              "-"}
                          </TableCell> */}
                          <TableCell>
                            <Chip label={statusLabel(s.status)} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              <Tooltip
                                title={perms.canUpdate() ? "Edit" : "View"}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => openModal(s)}
                                >
                                  {perms.canUpdate() ? (
                                    <EditRounded fontSize="small" />
                                  ) : (
                                    <VisibilityRounded fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {perms.canDelete() && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDelete(s.id)}
                                  >
                                    <DeleteRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {!loading && filtered.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <AssessmentRounded
                    sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    No sales records found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm || statusFilter !== "all"
                      ? "Try adjusting your search or filter criteria"
                      : "Create your first sales record to get started"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Modal */}
          <AnimatePresence>
            {open && (
              <Dialog open onClose={closeModal} maxWidth="lg" fullWidth>
                <DialogTitle
                  sx={{
                    background:
                      "linear-gradient(135deg,#005b82 0%,#2586bc 100%)",
                    color: "white",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PaymentsRounded />
                    <Typography variant="h6">
                      {editingSale
                        ? "Edit Sales Record"
                        : "Create Sales Record"}
                    </Typography>
                  </Box>
                </DialogTitle>

                <Box sx={{ px: 2, pt: 2 }}>
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {[
                      "Sales Details",
                      "Accounts Information",
                      "Additional Info",
                    ].map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                <DialogContent>
                  {activeStep === 0 && (
                    <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          name="sr_no"
                          label="SR No *"
                          value={saleForm.sr_no}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                        <TextField
                          name="vessel_name"
                          label="Vessel Name *"
                          value={saleForm.vessel_name}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                      </Box>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        {/* RFQ Date */}
                        <TextField
                          name="received_rfq_date"
                          label="RFQ Date"
                          type="date"
                          value={saleForm.received_rfq_date || ""}
                          onChange={onChange}
                          fullWidth
                          disabled={!canEditSalesFields}
                          InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                          name="received_rfq_from_client"
                          label="RFQ No"
                          value={saleForm.received_rfq_from_client}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                        <TextField
                          name="rfq_description"
                          label="RFQ Description"
                          value={saleForm.rfq_description}
                          onChange={onChange}
                          fullWidth
                          multiline
                          // rows={2}
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                        <TextField
                          name="issued_quotation_to_client"
                          label="Quotation No"
                          value={saleForm.issued_quotation_to_client}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                      </Box>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          name="received_po_from_client"
                          label="PO No"
                          value={saleForm.received_po_from_client}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                        <TextField
                          name="commited_delivery_date_to_client"
                          label="Committed Delivery Date"
                          type="date"
                          value={
                            saleForm.commited_delivery_date_to_client || ""
                          }
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: "bold" },
                          }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        />
                      </Box>
                      <TextField
                        name="logistic_status"
                        label="AWB No / Shipping Details"
                        value={saleForm.logistic_status}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{
                          sx: { fontWeight: "bold" }, // <-- Make label bold
                        }}
                        // disabled={!perms.canEditSales() || !perms.canCreate() }
                        disabled={!canEditSalesFields}
                      />
                      <TextField
                        name="sales_remark"
                        label="Sales Remark"
                        value={saleForm.sales_remark}
                        onChange={onChange}
                        fullWidth
                        multiline
                        // rows={2}
                        InputLabelProps={{
                          sx: { fontWeight: "bold" }, // <-- Make label bold
                        }}
                        // disabled={!perms.canEditSales() || !perms.canCreate() }
                        disabled={!canEditSalesFields}
                      />
                      <FormControl fullWidth>
                        <InputLabel
                          sx={{ fontWeight: "bold" }}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        >
                          Sales Status
                        </InputLabel>
                        <Select
                          name="sales_status"
                          value={saleForm.sales_status}
                          label="Sales Status"
                          onChange={onChange}
                          // disabled={!perms.canEditSales() || !perms.canCreate() }
                          disabled={!canEditSalesFields}
                        >
                          {STATUS_OPTS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {activeStep === 1 && (
                    <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
                      <TextField
                        name="issue_pi_to_client"
                        label="PI No"
                        value={saleForm.issue_pi_to_client}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{
                          sx: { fontWeight: "bold" }, // <-- Make label bold
                        }}
                        // disabled={!perms.canEditAccounts()}
                        disabled={!canEditAccountsFields}
                      />

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{ fontWeight: "bold" }}
                            // disabled={!perms.canEditAccounts()}
                            disabled={!canEditAccountsFields}
                          >
                            Select Currency
                          </InputLabel>
                          <Select
                            name="currency_advance"
                            value={saleForm.currency_advance}
                            label="Advance Currency"
                            onChange={onChange}
                            // disabled={!perms.canEditAccounts()}
                            disabled={!canEditAccountsFields}
                          >
                            <MenuItem value="">
                              <em>Select Currency</em>
                            </MenuItem>
                            {currencies.map((cur) => (
                              <MenuItem key={cur.id} value={cur.id}>
                                {cur.code} - {cur.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          name="advance_amount_received_from_client"
                          label="Advance Amount"
                          value={saleForm.advance_amount_received_from_client}
                          onChange={onChange}
                          fullWidth
                          // InputProps={{
                          //   startAdornment: (
                          //     <InputAdornment position="start">
                          //       <AttachMoneyRounded />
                          //     </InputAdornment>
                          //   ),
                          // }}
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditAccounts()}
                          disabled={!canEditAccountsFields}
                        />
                      </Box>

                      <TextField
                        name="advance_amount_received_date"
                        label="Advance Amount Received Date"
                        type="date"
                        value={saleForm.advance_amount_received_date || ""}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: "bold" },
                        }}
                        // disabled={!perms.canEditAccounts()}
                        disabled={!canEditAccountsFields}
                      />

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          name="final_tax_invoice_issued_to_client"
                          label="Final Tax Invoice No"
                          value={saleForm.final_tax_invoice_issued_to_client}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{ sx: { fontWeight: "bold" } }}
                          // disabled={!perms.canEditAccounts()}
                          disabled={!canEditAccountsFields}
                        />
                        <TextField
                          name="final_invoice_date"
                          label="Final Invoice Date"
                          type="date"
                          value={saleForm.final_invoice_date || ""}
                          onChange={onChange}
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: "bold" },
                          }}
                          // disabled={!perms.canEditAccounts()}
                          disabled={!canEditAccountsFields}
                        />
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{ fontWeight: "bold" }}
                            // disabled={!perms.canEditAccounts()}
                            disabled={!canEditAccountsFields}
                          >
                            Select Currency
                          </InputLabel>
                          <Select
                            name="currency_logistic"
                            value={saleForm.currency_logistic}
                            label="Logistic Currency"
                            onChange={onChange}
                            // disabled={!perms.canEditAccounts()}
                            disabled={!canEditAccountsFields}
                          >
                            <MenuItem value="">
                              <em>Select Currency</em>
                            </MenuItem>
                            {currencies.map((cur) => (
                              <MenuItem key={cur.id} value={cur.id}>
                                {cur.code} - {cur.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          name="logistic_cost"
                          label="Logistic Cost"
                          value={saleForm.logistic_cost}
                          onChange={onChange}
                          fullWidth
                          // InputProps={{
                          //   startAdornment: (
                          //     <InputAdornment position="start">
                          //       <AttachMoneyRounded />
                          //     </InputAdornment>
                          //   ),
                          // }}
                          InputLabelProps={{
                            sx: { fontWeight: "bold" }, // <-- Make label bold
                          }}
                          // disabled={!perms.canEditAccounts()}
                          disabled={!canEditAccountsFields}
                        />
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{ fontWeight: "bold" }}
                            // disabled={!perms.canEditAccounts()}
                            disabled={!canEditAccountsFields}
                          >
                            Select Currency
                          </InputLabel>
                          <Select
                            name="currency_final_payment"
                            value={saleForm.currency_final_payment}
                            label="Final Payment Currency"
                            onChange={onChange}
                            // disabled={!perms.canEditAccounts()}
                            disabled={!canEditAccountsFields}
                          >
                            <MenuItem value="">
                              <em>Select Currency</em>
                            </MenuItem>
                            {currencies.map((cur) => (
                              <MenuItem key={cur.id} value={cur.id}>
                                {cur.code} - {cur.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          name="final_payment_received_from_client"
                          label="Final Payment"
                          value={saleForm.final_payment_received_from_client}
                          onChange={onChange}
                          fullWidth
                          // InputProps={{
                          //   startAdornment: (
                          //     <InputAdornment position="start">
                          //       <AttachMoneyRounded />
                          //     </InputAdornment>
                          //   ),
                          // }}
                          InputLabelProps={{ sx: { fontWeight: "bold" } }}
                          // disabled={!perms.canEditAccounts()}
                          disabled={!canEditAccountsFields}
                        />
                      </Box>

                      <TextField
                        name="final_payment_received_date"
                        label="Final Payment Received Date"
                        type="date"
                        value={saleForm.final_payment_received_date || ""}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: "bold" },
                        }}
                        // disabled={!perms.canEditAccounts()}
                        disabled={!canEditAccountsFields}
                      />

                      <TextField
                        name="final_tax_invoice_status"
                        label="Final Tax Invoice Status"
                        value={saleForm.final_tax_invoice_status}
                        onChange={onChange}
                        fullWidth
                        InputLabelProps={{ sx: { fontWeight: "bold" } }}
                        // disabled={!perms.canEditAccounts()}
                        disabled={!canEditAccountsFields}
                      />

                      <TextField
                        name="accounts_remark"
                        label="Accounts Remark"
                        value={saleForm.accounts_remark}
                        onChange={onChange}
                        fullWidth
                        multiline
                        // rows={2}
                        InputLabelProps={{ sx: { fontWeight: "bold" } }}
                        // disabled={!perms.canEditAccounts()}
                        disabled={!canEditAccountsFields}
                      />

                      <FormControl fullWidth>
                        <InputLabel
                          sx={{ fontWeight: "bold" }}
                          // disabled={!perms.canEditAccounts()}
                          disabled={!canEditAccountsFields}
                        >
                          Accounts Status
                        </InputLabel>
                        <Select
                          name="accounts_status"
                          value={saleForm.accounts_status}
                          label="Accounts Status"
                          onChange={onChange}
                        >
                          {STATUS_OPTS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {activeStep === 2 && (
                    <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
                      {/* <TextField
                        name="remark"
                        label="Remark"
                        value={saleForm.remark}
                        onChange={onChange}
                        fullWidth
                        multiline
                         rows={3}
                        InputLabelProps={{ sx: { fontWeight: 'bold' } }}
                      /> */}

                      <FormControl fullWidth>
                        <InputLabel sx={{ fontWeight: "bold" }}>
                          Overall Status
                        </InputLabel>
                        <Select
                          name="status"
                          value={saleForm.status}
                          label="Overall Status"
                          onChange={onChange}
                        >
                          {STATUS_OPTS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {editingSale && (
                        <Card
                          variant="outlined"
                          sx={{ p: 2, background: alpha("#005b82", 0.03) }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            <b>Created by:</b> {editingSale.created_by_name}{" "}
                            &nbsp;|&nbsp; <b>Created:</b>{" "}
                            {new Date(editingSale.created_at).toLocaleString()}{" "}
                            &nbsp;|&nbsp; <b>Updated:</b>{" "}
                            {new Date(editingSale.updated_at).toLocaleString()}
                          </Typography>
                        </Card>
                      )}
                    </Box>
                  )}
                </DialogContent>

                <DialogActions>
                  <Button onClick={closeModal}>Cancel</Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((s) => s - 1)}
                    variant="outlined"
                  >
                    Back
                  </Button>
                  {activeStep < 2 ? (
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep((s) => s + 1)}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button variant="contained" onClick={onSubmit}>
                      {editingSale ? "Update Record" : "Create Record"}
                    </Button>
                  )}
                </DialogActions>
              </Dialog>
            )}
          </AnimatePresence>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default SalesTracker;
